// The material graph, compiled to TSL nodes instead of GLSL strings.
//
// WHY THIS EXISTS. `onBeforeCompile` — the hook the GLSL backend injects
// through — is never called under `WebGPURenderer`, on either of its backends
// (zero references to it in three's `renderers/webgpu/` or `materials/nodes/`).
// And GLSL cannot be kept: WebGPU parses every shader as WGSL, so
// `createShaderModule({code: "#version 450 …"})` fails at character one, TSL's
// `glslFn` is WebGL-backend-only, and pre-transpiling is impossible here because
// graphs are RUNTIME DATA (`material_assets.data`, authored and live-edited by
// users) rather than fixed shaders.
//
// TSL is what makes one description serve both: three ships
// `webgl-fallback/nodes/GLSLNodeBuilder` AND `webgpu/nodes/WGSLNodeBuilder`, so
// these node trees emit GLSL for the WebGL fallback and WGSL for the WebGPU
// default with no toolchain shipped to the client.
//
// SCOPE — EVERY node type the editor offers, which `tests/unit/materialGraphTSL
// .test.mjs` now asserts against the registry rather than against the fixtures,
// so a node added without an emitter fails there instead of the first time
// someone authors a material with it. An uncovered type is an ERROR, never an
// approximation: a plausible default is how a material stops looking like itself
// with nothing on screen to say so.
//
// VERIFIED BY PIXELS BEFORE IT SHIPPED, against the GLSL compiler it replaced:
// each of eight fixtures rendered through both backends on one sphere and
// diffed in LINEAR light. A constants-only graph agreed BIT FOR BIT, the
// pure-maths graphs to 1e-5, and the three real ones (textures plus an authored
// `specular`) to 3e-3, with a named cause for each residual rather than a
// tolerance. `docs/OGS_ARCHITECTURE.md` keeps the table and what it settled —
// including that the floor was once NOT zero and the reason was this file
// building the wrong material class, not either node's maths.
//
// THAT HARNESS IS GONE WITH THE SECOND COMPILER, and the lesson it left is
// worth more than the numbers: it had its OWN COPY of the node-material builder,
// so the one thing it could not measure was the builder — the class choice was
// compared against itself. `buildNodeMaterialFromGraph` below is now the ONE
// builder, called by the product.
//
// `GaussianColor` is covered but UNVERIFIABLE here: it reads the per-Gaussian
// attribute only the splat shading path supplies, so it cannot be exercised
// until that path moves to node materials as well.
//
// THIS IS THE ONLY COMPILER. There is no validator beside it any more: the
// editors call `compileMaterialGraphToTSL` for a graph's `errors` and its
// exposed params, so what they accept is by construction what the renderer
// draws.

import * as TSL from 'three/tsl'
// The node-material classes. `three/webgpu` is named here and in
// `threeSplatDeps.js` — the materials subsystem and the splat renderer each name
// their own entry point, and both specifiers are already in Vite's
// `optimizeDeps.include` (node_modules is not specifier-rewritten, so a missing
// entry reaches the browser unresolved).
import { MeshStandardNodeMaterial, MeshPhysicalNodeMaterial } from 'three/webgpu'
// Read ONLY for input/output types and defaults — the shapes both backends must
// agree on. `materialNodes.js` is pure (no THREE, no DOM), so importing it here
// costs nothing and keeps a graph's defaults from drifting between the two.
import { NODE_TYPES } from './materialNodes.js'
// Blender's fractal noise, the one substantial maths port. Its own module
// because it is ~200 lines of shader maths against a GLSL reference, and
// nothing else in this table needs more than a few.
import { NOISE_FRACTAL_TSL, noiseDistort2, randOff2 } from './materialNoiseTSL.js'

const {
  float, vec2, vec3, vec4, uniform, texture, uv, mix, clamp, dot, length,
  normalize, pow, sqrt, smoothstep, step, abs: tslAbs, sign: tslSign, floor: tslFloor,
  ceil: tslCeil, fract, mod, sin, cos, tan, atan, exp, log, cross, reflect, distance,
  max: tslMax, min: tslMin, positionWorld, normalWorld, tangentWorld,
  positionViewDirection, time, select, dFdx, dFdy, positionView, normalView,
  mat3, attribute,
} = TSL

const DIM = { float: 1, vec2: 2, vec3: 3, vec4: 4 }

/** The GLSL backend's `coerce`, in nodes. Same rules, so a graph that relied on
 *  an implicit float→vec3 or vec3→float in one backend gets it in the other. */
function coerce(node, fromType, toType) {
  if (!node || fromType === toType) return node
  const f = DIM[fromType] ?? 1
  const t = DIM[toType] ?? 1
  if (f === t) return node
  if (f === 1) return toType === 'vec2' ? vec2(node) : toType === 'vec4' ? vec4(node) : vec3(node)
  if (t === 1) return node.x
  if (t < f) return t === 2 ? node.xy : node.xyz
  if (f === 2 && t === 3) return vec3(node, float(0))
  if (f === 2 && t === 4) return vec4(node, float(0), float(0))
  if (f === 3 && t === 4) return vec4(node, float(1))
  return node
}

/** The GLSL backend's `inlineValue`: widen a scalar to the input's dimension. */
function inlineValue(raw, type) {
  const n = DIM[type] ?? 1
  if (n === 1) return Array.isArray(raw) ? Number(raw[0]) : Number(raw)
  if (Array.isArray(raw)) return raw
  const v = Number(raw)
  return Array.from({ length: n }, () => v)
}

const asNode = (value, type) => {
  const n = DIM[type] ?? 1
  if (n === 1) return float(Number(value ?? 0))
  const a = Array.isArray(value) ? value : [value, value, value, value]
  if (n === 2) return vec2(...a.slice(0, 2).map(Number))
  if (n === 4) return vec4(...a.slice(0, 4).map(Number))
  return vec3(...a.slice(0, 3).map(Number))
}

/** Unreal's ROUNDED Rec.601 weights, the same constants the GLSL Desaturation
 *  uses — the migration's bounded error argument depends on them matching. */
const LUMA_601 = [0.3, 0.59, 0.11]

/**
 * One entry per node type: `(i, ctx) => node`, where `i.<handle>` is the already
 * resolved input node and `ctx` carries the raw node + uniform helpers.
 * Most are one line because TSL has the operator; the ones that are not are the
 * ones a port has to think about, and they are marked.
 */
/** `aos_perturbNormal`: a tangent frame from screen-space derivatives of the
 *  VIEW position and the uv, then mapN into it. V is the GLSL helper's
 *  `-vViewPosition`, which is the view-space position. */
function perturbNormalView(N, mapN, texUv) {
  const q0 = dFdx(positionView), q1 = dFdy(positionView)
  const st0 = dFdx(texUv), st1 = dFdy(texUv)
  const S = normalize(q0.mul(st1.y).sub(q1.mul(st0.y)))
  const T = normalize(q0.negate().mul(st1.x).add(q1.mul(st0.x)))
  return normalize(mat3(S, T, N).mul(mapN))
}

/**
 * One entry per node type: `(i, ctx) => out`, where `i.<handle>` is the already
 * resolved input node.
 *
 * `out` is EITHER a single node (the declared output type) OR an object keyed by
 * output handle, whose values may be `{ node, type }` when the type is not the
 * declared one. Both forms are needed by real nodes: `TextureSample` has two
 * outputs (`rgb`, `a`), and `ComponentMask`'s output type DEPENDS ON ITS DATA —
 * it declares vec3 but emits float/vec2/vec3 according to which channels are
 * masked in, and getting that wrong silently coerces everything downstream.
 */
export const TSL_NODES = {
  // ---- constants -------------------------------------------------------
  // `paramName` makes it an Unreal-style named parameter — keyed by NAME, so two
  // converted constants sharing one resolve to a SINGLE uniform. Otherwise it is
  // just its `value` input, which the lazy resolver promotes to a uniform.
  // GATED ON `data.param`, THE BOOLEAN — not on `paramName` being non-empty.
  // The GLSL emitters read the flag (`materialNodes.js`), and gating on the name
  // instead diverges on a node that carries a leftover `paramName` with the flag
  // off: plain constant there, named parameter here. That is silent in the worst
  // way — the value renders the same, while two such nodes SHARING a name
  // collapse to one uniform on this path (`namedParam` dedupes by name, by
  // design) and the inspector grows a control the other backend has not got.
  ConstantFloat: (i, c) => (c.node.data?.param
    ? c.namedParam(c.node.data.paramName, c.node.data.value ?? 0, 'float') : i.value),
  ConstantColor: (i, c) => (c.node.data?.param
    ? c.namedParam(c.node.data.paramName, c.node.data.value ?? [1, 1, 1], 'vec3') : i.value),

  // ---- maths (1:1 with TSL) --------------------------------------------
  Multiply: (i) => i.a.mul(i.b),
  Add: (i) => i.a.add(i.b),
  Subtract: (i) => i.a.sub(i.b),
  Divide: (i) => i.a.div(i.b),
  OneMinus: (i) => float(1).sub(i.in),
  Saturate: (i) => clamp(i.in, 0, 1),
  Power: (i) => pow(tslMax(i.base, float(0)), i.exp),
  Sqrt: (i) => sqrt(tslMax(i.in, float(0))),
  Dot: (i) => dot(i.a, i.b),
  Length: (i) => length(i.in),
  Lerp: (i) => mix(i.a, i.b, i.alpha),
  Smoothstep: (i) => smoothstep(i.edge0, i.edge1, i.value),
  // The denominator is guarded, as the GLSL emitter's was: an author who leaves
  // inMin === inMax (the node's own defaults are 0 and 1, but both are editable
  // and both are commonly wired) otherwise divides by zero, and the NaN spreads
  // through every downstream slot — a black or invisible material, with the
  // graph reporting no error at all.
  Remap: (i) => i.value.sub(i.inMin).div(tslMax(i.inMax.sub(i.inMin), float(1e-5)))
    .mul(i.outMax.sub(i.outMin)).add(i.outMin),

  // ---- geometry / varyings ---------------------------------------------
  UV: (i, c) => (Number(c.node.data?.channel ?? 0) === 1 ? uv(1) : uv()),
  WorldNormal: () => normalWorld,
  // three's `positionViewDirection` already points surface->camera, which is what
  // the GLSL node emits as normalize(cameraPosition - vGraphWorldPos).
  ViewDirection: () => positionViewDirection,
  // Two outputs. The bitangent follows glTF's own rule — cross(N, T) * T.w — the
  // same one `hairStrandSkin` and three use, so a graph reading `bitangent` gets
  // the handedness the mesh was authored with rather than a guess.
  WorldTangent: () => ({
    tangent: normalize(tangentWorld.xyz),
    bitangent: normalize(normalWorld.cross(tangentWorld.xyz).mul(tangentWorld.w)),
  }),

  // ---- swizzles / construction -----------------------------------------
  // Output TYPE depends on the mask, matching the GLSL emitter exactly: an empty
  // mask falls back to `r` there, so it does here.
  ComponentMask: (i, c) => {
    const d = c.node.data || {}
    let chans = ['r', 'g', 'b'].filter((ch) => d[`mask_${ch}`] !== false)
    if (chans.length === 0) chans = ['r']
    const swizzle = chans.join('').replace(/r/g, 'x').replace(/g/g, 'y').replace(/b/g, 'z')
    const type = chans.length === 1 ? 'float' : chans.length === 2 ? 'vec2' : 'vec3'
    return { out: { node: i.in[swizzle], type } }
  },
  MakeVector2: (i) => vec2(i.x, i.y),

  // ---- colour adjust (Unreal parity — see migratedAdjustChain.test.mjs) ----
  // Desaturation: lerp toward luma by `fraction`, Unreal's ROUNDED 601 weights.
  // The migration's bounded-error argument depends on these exact constants.
  Desaturation: (i) => {
    const luma = dot(i.color, vec3(...LUMA_601))
    return mix(i.color, vec3(luma), i.fraction)
  },
  // BrightContrast: contrast about 0.5, then brightness.
  BrightContrast: (i) => i.color.sub(float(0.5)).mul(i.contrast.add(float(1)))
    .add(float(0.5)).add(i.bright),

  // ---- maths, part 2 (all 1:1) -----------------------------------------
  Normalize: (i) => normalize(i.in),
  Sine: (i) => sin(i.in),
  Cosine: (i) => cos(i.in),
  Tangent: (i) => tan(i.in),
  ArcTan2: (i) => atan(i.y, i.x),
  Exp: (i) => exp(i.in),
  Log: (i) => log(tslMax(i.in, float(1e-6))),
  Abs: (i) => tslAbs(i.in),
  Sign: (i) => tslSign(i.in),
  Floor: (i) => tslFloor(i.in),
  Ceil: (i) => tslCeil(i.in),
  Fract: (i) => fract(i.in),
  Mod: (i) => mod(i.a, i.b),
  Min: (i) => tslMin(i.a, i.b),
  Max: (i) => tslMax(i.a, i.b),
  Clamp: (i) => clamp(i.value, i.min, i.max),
  Step: (i) => step(i.edge, i.value),
  Distance: (i) => distance(i.a, i.b),
  Cross: (i) => cross(i.a, i.b),
  Reflect: (i) => reflect(i.in, i.normal),
  // Rec.709, unlike Desaturation's rounded 601 — the two node types genuinely
  // use different weights in the GLSL backend and that is not a typo.
  Luminance: (i) => dot(i.in, vec3(0.2126, 0.7152, 0.0722)),
  Gamma: (i) => pow(tslMax(i.in, vec3(0)), vec3(i.gamma)),
  Time: () => time,
  Panner: (i) => (i.uv ?? uv()).add(i.speed.mul(time)),
  WorldPosition: () => positionWorld,

  // ---- vector plumbing --------------------------------------------------
  Split: (i) => ({ r: i.in.x, g: i.in.y, b: i.in.z }),
  Combine: (i) => vec3(i.r, i.g, i.b),
  BreakVector2: (i) => ({ x: i.in.x, y: i.in.y }),
  MakeVector4: (i) => vec4(i.x, i.y, i.z, i.w),
  Rotate2D: (i) => {
    const d = i.uv.sub(i.center)
    const sn = sin(i.angle), cs = cos(i.angle)
    return i.center.add(vec2(d.x.mul(cs).sub(d.y.mul(sn)), d.x.mul(sn).add(d.y.mul(cs))))
  },

  // ---- shading --------------------------------------------------------
  // Schlick. The GLSL node dots vNormal against vViewPosition — both VIEW space —
  // and a dot is invariant under the shared rotation, so doing it in world space
  // here is the same number, not an approximation.
  Fresnel: (i) => pow(float(1).sub(tslMax(dot(normalize(normalWorld), normalize(positionViewDirection)), float(0))),
    i.exponent).mul(float(1).sub(i.f0)).add(i.f0),
  // Unreal's BlendAngleCorrectedNormals, verbatim. The max() guards a base
  // normal pointing straight sideways (n1.z -> 0).
  BlendAngleCorrectedNormals: (i) => {
    const n1 = i.base.add(vec3(0, 0, 1))
    const n2 = i.detail.mul(vec3(-1, -1, 1))
    return n1.mul(dot(n1, n2)).div(tslMax(n1.z, float(1e-4))).sub(n2)
  },
  // Piecewise-linear, built from the node's own stops exactly as the GLSL
  // emitter does: each segment ramps 0->1 across [p_i, p_i+1] and CLAMPS at 1 so
  // later mixes take over.
  ColorRamp: (i, c) => {
    const raw = Array.isArray(c.node.data?.stops) && c.node.data.stops.length >= 2
      ? c.node.data.stops
      : [{ pos: 0, color: [0, 0, 0] }, { pos: 1, color: [1, 1, 1] }]
    const stops = [...raw].sort((a, b) => (a.pos ?? 0) - (b.pos ?? 0))
    const col = (x) => vec3(...(Array.isArray(x) ? x : [0, 0, 0]).slice(0, 3).map(Number))
    const t = clamp(i.fac, 0, 1)
    let out = col(stops[0].color)
    for (let k = 1; k < stops.length; k++) {
      const p0 = Number(stops[k - 1].pos ?? 0)
      const p1 = Number(stops[k].pos ?? 1)
      const denom = (p1 - p0) || 1e-5
      out = mix(out, col(stops[k].color), clamp(t.sub(float(p0)).div(float(denom)), 0, 1))
    }
    return { color: out }
  },

  // ---- Blender rgb<->hsv, branch for branch ----------------------------
  // Written with select() rather than the branchless "K vector" trick, because
  // the branchless form differs from Blender's at the achromatic edge (cdelta 0)
  // and this has to MATCH the GLSL helper, not merely be a correct hsv.
  HueSatVal: (i) => {
    const c = i.color
    const cmax = tslMax(c.x, tslMax(c.y, c.z))
    const cmin = tslMin(c.x, tslMin(c.y, c.z))
    const cdelta = cmax.sub(cmin)
    // Blender guards `if (cmax != 0)` / `if (s != 0)`; the divisors are only ever
    // read under those guards, so the epsilon here only keeps the UNTAKEN branch
    // from producing a NaN that select() would still have to carry.
    const safeMax = tslMax(cmax, float(1e-20))
    const safeDelta = tslMax(cdelta, float(1e-20))
    const sat0 = select(cmax.equal(float(0)), float(0), cdelta.div(safeMax))
    const cc = vec3(cmax).sub(c).div(safeDelta)
    const hRed = cc.z.sub(cc.y)
    const hGreen = float(2).add(cc.x).sub(cc.z)
    const hBlue = float(4).add(cc.y).sub(cc.x)
    let h = select(c.x.equal(cmax), hRed, select(c.y.equal(cmax), hGreen, hBlue)).div(float(6))
    h = select(h.lessThan(float(0)), h.add(float(1)), h)
    h = select(sat0.equal(float(0)), float(0), h)

    // The node's own adjustment, in the order the GLSL chunk applies it.
    const h2 = fract(h.add(i.hue).add(float(0.5)))
    const s2 = clamp(sat0.mul(i.saturation), 0, 1)
    const v2 = cmax.mul(i.value)

    // hsv -> rgb, Blender's six-sector form.
    const hh = select(h2.equal(float(1)), float(0), h2).mul(float(6))
    const idx = tslFloor(hh)
    const f = hh.sub(idx)
    const pp = v2.mul(float(1).sub(s2))
    const q = v2.mul(float(1).sub(s2.mul(f)))
    const t = v2.mul(float(1).sub(s2.mul(float(1).sub(f))))
    const bySector = select(idx.equal(float(0)), vec3(v2, t, pp),
      select(idx.equal(float(1)), vec3(q, v2, pp),
        select(idx.equal(float(2)), vec3(pp, v2, t),
          select(idx.equal(float(3)), vec3(pp, q, v2),
            select(idx.equal(float(4)), vec3(t, pp, v2), vec3(v2, pp, q))))))
    const rgb = select(s2.equal(float(0)), vec3(v2), bySector)
    return mix(c, rgb, i.fac)
  },

  // `aos_perturbNormal` verbatim, and the ONE place that frame is built — both
  // this node and NormalMap go through it, exactly as both GLSL emitters call
  // the one helper.
  PerturbNormal: (i) => perturbNormalView(normalize(normalView), i.tangent, i.uv ?? uv()),

  // The per-Gaussian colour the SPLAT shading path supplies. On a mesh there is
  // no such attribute, and the GLSL assembler declares a white fallback for
  // exactly that case — so this is only meaningful once the splat material moves
  // to node materials too.
  GaussianColor: () => attribute('vGaussianColor', 'vec3'),

  // ---- textures --------------------------------------------------------
  // TWO outputs, and colour space is NOT handled here. The GLSL backend converts
  // sRGB->linear in the shader; on this path the TEXTURE carries its own
  // `colorSpace` and three converts, so a port must set that on the texture row
  // rather than emit a pow() — the one place the two backends genuinely differ in
  // where the work happens.
  TextureSample: (i, c) => {
    // `colorSpace: 'data'` is Blender's Non-Color — the same field, read the same
    // way as the GLSL emitter reads it (materialNodes.js).
    const t = c.sampleTexture(i.uv ?? uv(), { linear: c.node.data?.colorSpace === 'data' })
      // Unbound -> vec3(1) / alpha 1, the IDENTITY. three reads a null sampler as
      // black, which is why this is explicit on both backends.
      ?? vec4(1, 1, 1, 1)
    return { rgb: { node: t.xyz, type: 'vec3' }, a: { node: t.w, type: 'float' } }
  },
  // VIEW SPACE, because that is where the `normal` slot is consumed: three's
  // `normalNode` falls back to `materialNormal`, which is `normalView`, and the
  // GLSL emitter likewise perturbs the geometry `normal` already in scope after
  // <normal_fragment_maps>. Building this from `normalWorld`/`tangentWorld`
  // instead — which is what a first pass does, because those read like the
  // "real" vectors — puts a world-space normal into a view-space slot: the
  // lighting still resolves, the sphere still looks lit, and the shading is
  // simply wrong. The pixel A/B is what caught it.
  //
  // Unbound is the GEOMETRY normal, matching the GLSL node's `vec3 o = normal;`
  // — the same identity rule an unbound TextureSample follows.
  NormalMap: (i, c) => {
    const N = normalize(normalView)
    const texUv = i.uv ?? uv()
    // ALWAYS linear, whatever the row says — a normal map is data, and the GLSL
    // emitter hardcodes `{ linear: true }` here for the same reason.
    const sampled = c.sampleTexture(texUv, { linear: true })
    if (!sampled) return { out: N, tangent: vec3(0, 0, 1) }
    const packed = sampled.xyz.mul(float(2)).sub(float(1))
    const mapN = vec3(packed.xy.mul(i.strength), packed.z)
    return { out: perturbNormalView(N, mapN, texUv), tangent: mapN }
  },

  // ---- noise -----------------------------------------------------------
  // The fractal and the normalize flag are BOTH `node.data`, so they are settled
  // in JS and never reach the shader as a branch — the same specialisation the
  // GLSL emitter does by picking a helper name and writing `0.0`/`1.0`.
  //
  // The two extra `color` channels are emitted unconditionally, exactly as the
  // GLSL does. There they survive only because the compiler's dead-code pass
  // removes them when `color` is unwired; here nothing references the node unless
  // something reads that handle, so an unused channel is never built at all.
  Noise: (i, c) => {
    const fractal = NOISE_FRACTAL_TSL[c.node.data?.noiseType] ?? NOISE_FRACTAL_TSL.fbm
    const nrm = float(c.node.data?.normalize === false ? 0 : 1)
    const p = noiseDistort2((i.uv ?? uv()).mul(i.scale), i.distortion).toVar()
    const tail = [i.detail, i.roughness, i.lacunarity, i.offset, i.gain, nrm]
    const fac = fractal(p, ...tail).toVar()
    return {
      fac: { node: fac, type: 'float' },
      color: {
        node: vec3(fac,
          fractal(p.add(randOff2(float(2))), ...tail),
          fractal(p.add(randOff2(float(3))), ...tail)),
        type: 'vec3',
      },
    }
  },
}

/** The output node's inputs, and which NodeMaterial slot each drives. Mirrors
 *  SLOT_TARGET in the GLSL backend; `ao` and `specular` are the two that do not
 *  map to a plain slot — see the notes on each. */
export const SLOT_TO_NODE_MATERIAL = {
  color: 'colorNode',
  metalness: 'metalnessNode',
  roughness: 'roughnessNode',
  emissive: 'emissiveNode',
  opacity: 'opacityNode',
  normal: 'normalNode',
  // AO has no assignable slot: the GLSL backend multiplies reflectedLight's
  // indirect terms after <aomap_fragment>. three exposes `aoNode` on
  // MeshStandardNodeMaterial, which is the node equivalent, but it applies to
  // indirect diffuse only — the GLSL chunk also scales indirect SPECULAR through
  // computeSpecularOcclusion. Flagged rather than silently dropped.
  ao: 'aoNode',
  // Specular is Unreal's dielectric reflectance; the GLSL backend writes F0 =
  // 0.08 * specular. MeshPhysicalNodeMaterial has `specularIntensityNode`, whose
  // convention is F0 = 0.08 * value — the same one — but it only exists on the
  // physical material, so a graph using it must build that class.
  specular: 'specularIntensityNode',
  // Physical-only, and NOT opt-in the way GLSL needs. The GLSL backend has to
  // switch these on at BUILD time (three's WebGLPrograms compiles USE_CLEARCOAT
  // only when material.clearcoat > 0, so writing the field otherwise is a GLSL
  // error) — which is why `buildGraphMaterial` sets clearcoat/anisotropy to 1 and
  // lets the graph's chunks overwrite them. On the node path assigning the slot
  // IS the opt-in, so that whole dance disappears.
  clearcoat: 'clearcoatNode',
  clearcoatRoughness: 'clearcoatRoughnessNode',
  // ONE node carries BOTH anisotropy inputs. three's `anisotropyNode` is a vec2
  // in tangent space whose LENGTH is the strength and whose direction is the
  // lobe's — so the graph's `anisotropy` (float) and `anisotropyDirection`
  // (vec3, .xy read) are folded together by `foldAnisotropy` below. There is
  // deliberately no `anisotropyDirection` entry: a second mapping onto the same
  // property would make the two slots overwrite each other by iteration order.
  anisotropy: 'anisotropyNode',
}

/** The graph's two anisotropy inputs -> three's single vec2 `anisotropyNode`.
 *
 *  `anisotropyDirection` had no mapping at all, so `buildNodeMaterialFromGraph`
 *  threw on any graph that wired one — and it threw from the BUILDER, past a
 *  compile that reported `errors: []`, so the mesh fell to the neutral
 *  placeholder with the graph editor insisting the graph was fine. It is the
 *  documented way to author hair cards (`docs/MATERIAL_GRAPH.md`), which is the
 *  one shape guaranteed to hit it.
 *
 *  Unwired direction keeps three's own: `vec2(strength, 0)` points along the
 *  mesh tangent, which is what `materialAnisotropy` resolves to there. A wired
 *  one is normalised with the same 1e-5 floor the rest of this file uses, so a
 *  zero-length direction (an unwritten tangent atlas texel) yields no lobe
 *  rather than a NaN across the whole surface.
 */
function foldAnisotropy(slots) {
  const dir = slots.anisotropyDirection
  const strength = slots.anisotropy
  delete slots.anisotropyDirection
  if (!strength) return
  if (!dir) { slots.anisotropy = vec2(strength, float(0)); return }
  const xy = dir.xy
  slots.anisotropy = xy.div(tslMax(length(xy), float(1e-5))).mul(strength)
}

/** Slots only a PHYSICAL node material carries. A standard one ignores them
 *  SILENTLY, which is not a small difference: the first run of the pixel harness
 *  reported the two clearcoat graphs at mean 2.2 and 4.7 against skin's 0.38,
 *  purely because the TSL arm was rendering without a clearcoat lobe the GLSL arm
 *  had. The GLSL backend picks its class from the same question
 *  (`needsClearcoat || needsAnisotropy` in `buildGraphMaterial`). */
export const PHYSICAL_ONLY = new Set(['clearcoat', 'clearcoatRoughness', 'anisotropy', 'specular'])

/**
 * The uniform key for an EXPOSED INPUT: `p_<nodeId>_<handle>`.
 *
 * NOT for `namedParam`, which keys on the display name on purpose — see the note
 * at its call site. The two mechanisms look alike and want opposite things: a
 * migrated graph's constants share a name because they are one control, while an
 * exposed input's name is a LABEL on one node's one input.
 *
 * KEYED ON THE NODE AND THE INPUT, NOT ON THE DISPLAY NAME, and the difference is
 * a silent data loss rather than a cosmetic one. A display name is user-editable
 * and not unique: two nodes exposing "Rim Power" collapsed into ONE uniform, so
 * one of the two parameters vanished from `params` entirely — it never reached the
 * inspector — and editing the survivor drove both nodes. Renaming a parameter also
 * changed the key, which is what `material-graph.spec.js` means by a STABLE
 * uniform.
 *
 * The display name is not lost: it rides `params[].name`, which is what the
 * inspector shows. Nothing addresses a param by uniform key — `setGraphParam`
 * resolves `(nodeId, handle)` — so the key's only jobs are uniqueness and
 * stability, and a name gives neither.
 */
function paramUniformName(nodeId, handle) {
  const safe = (v) => String(v).replace(/[^a-zA-Z0-9_]/g, '_')
  return `p_${safe(nodeId)}_${safe(handle)}`
}

/**
 * Compile a graph and hang its slots on a node material.
 *
 * THE ONE BUILDER, used by the product path (`avatarMaterial.js`) AND by the
 * pixel A/B. It was the harness's own for a while, and a harness that builds the
 * material a different way than the product does can only measure itself: the
 * A/B would stay green while the shipped path dropped a slot.
 *
 * @param {object} graph
 * @param {{textureFor?: (id: any, node: object, opts: object) => any}} [o]
 * @returns {{mat: object, compiled: object}}
 */
export function buildNodeMaterialFromGraph(graph, { textureFor = () => null } = {}) {
  const compiled = compileMaterialGraphToTSL(graph, { textureFor })
  if (compiled.errors.length) throw new Error('material graph (tsl): ' + compiled.errors.join('; '))

  // A physical-only slot the graph never AUTHORED does not get to pick the
  // material class. `specular` is the whole reason: it is emitted for every
  // graph (its declared default is 0.5, which is F0 = 0.08 * 0.5 = 0.04, exactly
  // three's own default) and `specularIntensityNode` exists only on the physical
  // material — so keying the class off its mere presence made EVERY graph
  // physical here while the GLSL backend built a standard material for the same
  // row. `clearcoat`/`anisotropy` need no such care: both backends already gate
  // them on the same opt-in rule, so their slots are absent unless driven.
  //
  // The fold runs BEFORE the class choice, and that ordering is the whole reason
  // `anisotropyDirection` is NOT in PHYSICAL_ONLY: by the time the class is
  // picked the slot is gone, folded into `anisotropy` or dropped with it. A
  // direction wired at zero strength is no lobe, so it must not force a
  // physical material on its own.
  foldAnisotropy(compiled.slots)
  const authored = (slot) => PHYSICAL_ONLY.has(slot) && !compiled.defaultOnlySlots.includes(slot)
  const needsPhysical = Object.keys(compiled.slots).some(authored)
  const mat = needsPhysical ? new MeshPhysicalNodeMaterial() : new MeshStandardNodeMaterial()
  for (const [slot, node] of Object.entries(compiled.slots)) {
    const prop = SLOT_TO_NODE_MATERIAL[slot]
    // These THROW rather than skip. Skipping is how a slot goes missing while the
    // render still looks plausible — the exact failure this whole port is guarded
    // against, so the builder must not have it either.
    if (!prop) throw new Error(`material graph (tsl): slot ${slot} has no NodeMaterial property`)
    if (!(prop in mat)) {
      // The ONE permitted drop, and it is provable rather than hopeful: the slot
      // carries its declared default, and for `specular` that default IS the
      // renderer's, so assigning it and omitting it are the same image.
      if (compiled.defaultOnlySlots.includes(slot)) continue
      throw new Error(`material graph (tsl): ${mat.type} has no ${prop} — slot ${slot} would be dropped`)
    }
    mat[prop] = node
  }

  // The node-path twin of the GLSL backend's `AOS_UNLIT` branch, which replaces
  // `outgoingLight` with `diffuseColor` just before `<opaque_fragment>`. Here the
  // equivalent is `material.fragmentNode`: set, the material outputs it DIRECTLY
  // and the lighting model is skipped entirely. Built here rather than at the
  // toggle, because by then the graph is gone — and handed back rather than
  // stashed, so `avatarMaterial` stays the one owner of what `userData` carries.
  //
  // Alpha comes along: `<opaque_fragment>` reads `diffuseColor.a`, so an unlit
  // view of a masked or translucent material must not silently go opaque.
  const unlitNode = vec4(compiled.slots.color ?? vec3(1, 1, 1), compiled.slots.opacity ?? float(1))

  return { mat, compiled, unlitNode }
}

// The output node of a material model that NO LONGER EXISTS. Kept as a bare
// string, deliberately not re-added to `materialNodes.js`: nothing may author,
// palette or render one, and the only thing left to do with it is recognise a
// stored row and say so. See the refusal in `compileMaterialGraphToTSL`.
const LEGACY_TOON = 'ToonBSDF'

/** Node types this spike does NOT cover, reported rather than approximated. */
export function unsupportedTypes(graph) {
  const types = new Set((graph?.nodes ?? []).map((n) => n.type))
  types.delete('PrincipledBSDF')
  return [...types].filter((t) => !TSL_NODES[t]).sort()
}

/**
 * Walk the graph and build one TSL node per output-node slot.
 * @returns {{slots: object, uniforms: object, params: Array, errors: string[],
 *   needsUv: boolean, needsTangent: boolean}}
 */
export function compileMaterialGraphToTSL(graph, { textureFor = () => null } = {}) {
  const errors = []
  const nodes = graph?.nodes ?? []
  const edges = graph?.edges ?? []
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const outNode = nodes.find((n) => n.type === 'PrincipledBSDF' || n.type === LEGACY_TOON)
  if (!outNode) {
    return {
      slots: {}, uniforms: {}, params: [], textureNodes: [], defaultOnlySlots: [],
      errors: ['no PrincipledBSDF output node'], needsUv: false, needsTangent: false,
    }
  }
  // A TOON ROW IS REFUSED BY NAME, because one still exists in the catalogue.
  //
  // `ToonBSDF` is gone: it is not in `materialNodes.js`, the New menu does not
  // offer it and nothing renders it. What is NOT gone is the data — a row whose
  // graph carries a ToonBSDF output can still be sitting on a character (there
  // is one, unbound, on isaac-ogs), and it will be fetched and compiled like any
  // other. Falling through to the generic "no PrincipledBSDF output node" would
  // be true and useless: it sends the author looking for a missing node rather
  // than telling them the material MODEL was removed.
  //
  // So this is the same treatment the legacy `pbr`/`toon`/`skin` ROW TYPES get —
  // named, explained, with the fix in the message — rather than a silent or a
  // generic failure. It is a data-migration message, not a feature flag.
  if (outNode.type === LEGACY_TOON) {
    return {
      slots: {}, uniforms: {}, params: [], textureNodes: [], defaultOnlySlots: [],
      errors: ['ToonBSDF was removed — this row predates that. '
        + 'Re-author it on PrincipledBSDF, or delete it if nothing binds it.'],
      needsUv: false, needsTangent: false,
    }
  }

  const incoming = (id, handle) => edges.find((e) => e.target === id && e.targetHandle === handle)
  const uniforms = {}
  const params = []
  /** Every `texture()` node built, so a caller can swap the texture behind one
   *  later (the quality-tier upgrade). The GLSL backend does not need this — its
   *  samplers ARE entries in `uniforms` and carry `textureId`. */
  const textureNodes = []
  const emitted = new Map()   // `${id}:${handle}` -> { node, type }
  const doneNodes = new Set()
  const visiting = new Set()
  let uid = 0
  let needsUv = false, needsTangent = false

  const defs = NODE_TYPES

  function emitNode(node) {
    // Keyed by NODE, not by an output handle called `out`: TextureSample's
    // outputs are `rgb`/`a` and WorldTangent's are `tangent`/`bitangent`, so an
    // `:out` key re-emitted them on every reference — duplicating their uniforms
    // and building a second texture() per use.
    if (doneNodes.has(node.id)) return
    if (visiting.has(node.id)) { errors.push(`cycle through node ${node.id}`); return }
    visiting.add(node.id)
    const def = defs[node.type]
    const build = TSL_NODES[node.type]
    if (!def || !build) {
      errors.push(`unsupported node type ${node.type}`)
      visiting.delete(node.id)
      return
    }
    if (node.type === 'UV' && Number(node.data?.channel ?? 0) !== 1) needsUv = true
    // DERIVED from the input's own `usesMeshUv`, never a hand-kept type list.
    // The GLSL backend kept one and it silently omitted `PerturbNormal`, which
    // emitted `vUv` into a shader that never declared it — the material failed to
    // compile and fell back to the neutral placeholder. A node that declares it
    // falls back to the mesh UVs is the only thing that can be asked here.
    for (const inp of def.inputs ?? []) {
      if (inp.usesMeshUv && !incoming(node.id, inp.handle)) needsUv = true
    }
    if (node.type === 'WorldTangent' || node.type === 'NormalMap') needsTangent = true

    // LAZY, like the GLSL backend's `ctx.varOf(handle)`: an input is resolved —
    // and an unwired one promoted to a uniform — only when the emitter actually
    // reads it. Resolving all of them eagerly created a second uniform for every
    // constant, because the emitter then asked for its own as well.
    const cache = new Map()
    const inputs = new Proxy({}, {
      get(_t, handle) {
        if (typeof handle !== 'string') return undefined
        if (!cache.has(handle)) {
          const inputDef = (def.inputs ?? []).find((x) => x.handle === handle)
          cache.set(handle, inputDef ? resolve(node, inputDef) : undefined)
        }
        return cache.get(handle)
      },
      has: (_t, handle) => (def.inputs ?? []).some((x) => x.handle === handle),
    })
    const ctx = {
      node,
      // KEYED ON THE NAME, DELIBERATELY, and this is the one place that is right.
      // The legacy migration mints a CONSTANT PER SLOT, so a graph carries several
      // `ConstantFloat` nodes all named "Roughness Factor" and the author means ONE
      // control. Keying these on the node would split that knob into four that
      // have to be dragged together. The `expose` path below is the opposite case
      // — see `paramUniformName`.
      namedParam: (rawName, value, type) => {
        const pname = (rawName != null && String(rawName).trim()) || 'Param'
        const name = `p_${pname.replace(/[^a-zA-Z0-9_]/g, '_')}`
        const v = type === 'float' ? Number(value) : value
        if (!uniforms[name]) uniforms[name] = { value: v, type, node: uniform(asNode(v, type)) }
        if (!params.some((x) => x.uniform === name)) {
          params.push({ uniform: name, nodeId: node.id, handle: 'value', type, name: pname, value: v })
        }
        return uniforms[name].node
      },
      // An UNBOUND texture is normal and must compile to the IDENTITY, not an
      // error and not black: the rig importer mints rows with `textureId: null`
      // on every slot the glTF did not fill, and the GLSL backend resolves a null
      // sampler the same way. Reading it as a failure would turn a freshly
      // imported character black.
      // `opts.linear` travels with the request, exactly as the GLSL backend's
      // `ctx.uniformSampler(name, id, { linear })` does, because the two backends
      // must not decide a texture's colour space in two places. On THIS path it
      // is the whole mechanism: the GLSL backend converts sRGB->linear in the
      // shader, while here the TEXTURE carries its own `colorSpace` and three
      // converts on sample. Same rule, different executor.
      sampler: (opts = {}) => {
        const id = node.data?.textureId
        return id != null ? (textureFor(id, node, opts) ?? null) : null
      },
      // Build the TextureNode AND REGISTER IT, because a texture on this path is
      // not a uniform the way it is on the GLSL one — it is baked into a
      // `texture()` node the caller has no other handle on. `avatarMaterial`'s
      // tier-upgrade pass swaps textures by walking `graphUniforms` for a
      // `.value` that is a Texture, and a TextureNode answers to exactly that,
      // so registering it here is what keeps texture quality working. Without
      // it the swap finds nothing and the character silently stays on the low
      // tier it booted with — which looks like the upgrade never being asked for.
      sampleTexture: (uvNode, opts = {}) => {
        const id = node.data?.textureId
        const tex = id != null ? (textureFor(id, node, opts) ?? null) : null
        if (!tex) return null
        const texNode = texture(tex, uvNode)
        textureNodes.push({ nodeId: node.id, textureId: id, linear: !!opts.linear, node: texNode })
        return texNode
      },
    }
    const out = build(inputs, ctx)
    const outs = def.outputs ?? [{ handle: 'out', type: 'vec3' }]
    const isMulti = out && typeof out === 'object' && !out.isNode
      && outs.some((o) => Object.prototype.hasOwnProperty.call(out, o.handle))
    for (const o of outs) {
      const raw = isMulti ? out[o.handle] : out
      if (raw == null) continue
      // `{ node, type }` lets an emitter declare a type its registry entry cannot
      // — ComponentMask's varies with its mask.
      const isTagged = typeof raw === 'object' && !raw.isNode && 'node' in raw
      emitted.set(`${node.id}:${o.handle}`, {
        node: isTagged ? raw.node : raw,
        type: isTagged ? raw.type : o.type,
      })
    }
    visiting.delete(node.id)
    doneNodes.add(node.id)
  }

  // `raw` is passed IN, already defaulted by the caller. Re-reading node.data
  // here instead dropped every input's DEFAULT — an unwired `strength: 1` became
  // 0, which is a material that still compiles and renders flat.
  function resolveInline(node, handle, type, raw) {
    const exposed = node.data?.expose?.[handle]
    const value = inlineValue(raw ?? (type === 'float' ? 0 : [0, 0, 0]), type)
    const name = exposed ? paramUniformName(node.id, handle) : `u_${handle}_${uid++}`
    const u = uniform(asNode(value, type))
    uniforms[name] = { value, type, node: u }
    if (exposed && !params.some((p) => p.uniform === name)) {
      params.push({ uniform: name, nodeId: node.id, handle, type, name: exposed, value })
    }
    return u
  }

  function resolve(node, inputDef) {
    const edge = incoming(node.id, inputDef.handle)
    if (edge) {
      const src = byId.get(edge.source)
      if (src) {
        emitNode(src)
        const rec = emitted.get(`${src.id}:${edge.sourceHandle}`)
        if (rec) return coerce(rec.node, rec.type, inputDef.type ?? rec.type)
      }
    }
    const raw = node.data?.[inputDef.handle] ?? inputDef.default
    if (raw == null) return null           // e.g. `normal`: leave three's own
    return resolveInline(node, inputDef.handle, inputDef.type ?? 'float', raw)
  }

  // Opt-in slots (clearcoat, anisotropy) follow their GROUP's gate handle, so a
  // clearcoatRoughness alone can never switch the lobe on — the same rule the
  // GLSL backend applies, and gating per-handle instead silently drops the
  // roughness of an enabled lobe.
  const outDef = defs[outNode.type]
  const optInCache = new Map()
  const optedIn = (group) => {
    if (optInCache.has(group)) return optInCache.get(group)
    const gate = outDef.inputs.find((x) => x.handle === group)
    const on = !!gate && (!!incoming(outNode.id, group)
      || Number(outNode.data?.[group] ?? gate.default ?? 0) > 0)
    optInCache.set(group, on)
    return on
  }

  /** Slots that are UNWIRED and still carry the declared default — i.e. the
   *  graph never authored them; they exist only because every output input is
   *  resolved. It matters for exactly one slot and the reason is three's API:
   *  `specularIntensityNode` lives on the PHYSICAL node material alone, while
   *  the GLSL backend writes specular F0 into a STANDARD material's shader
   *  perfectly well. So without this every graph would build a physical
   *  material on this path and a standard one on the other — a class divergence
   *  driven by a value nobody set. See `buildNodeMaterialFromGraph`. */
  const defaultOnlySlots = []
  const sameAsDefault = (raw, def) => {
    if (raw === undefined) return true
    if (Array.isArray(def)) return Array.isArray(raw) && def.length === raw.length
      && def.every((v, k) => Number(v) === Number(raw[k]))
    return Number(raw) === Number(def)
  }

  const slots = {}
  for (const inputDef of outDef.inputs ?? []) {
    if (inputDef.optInSlot && !optedIn(inputDef.optInSlot)) continue
    if (!incoming(outNode.id, inputDef.handle)
        && sameAsDefault(outNode.data?.[inputDef.handle], inputDef.default)) {
      defaultOnlySlots.push(inputDef.handle)
    }
    const value = resolve(outNode, inputDef)
    if (value) slots[inputDef.handle] = value
  }
  return { slots, uniforms, params, textureNodes, defaultOnlySlots, errors, needsUv, needsTangent }
}
